import { UploadedFile } from '@components/file-field';
import { createForm, useCreateFormStore } from '@components/form';
import { FormWithUnsavedChangesPrompt } from '@components/generic-modals';
import { HideContainer } from '@components/hide-container';
import { IconDate } from '@components/icons/date';
import { IconSend } from '@components/icons/send';
import { LabelTextGroup } from '@components/label-text-group';
import { Spinner } from '@components/loading-spinner';
import { NoResultCard } from '@components/no-result-card';
import { Sidebar } from '@components/sidebar';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { useScopedTranslate } from '@locale/translate';
import { getRawTextFromHtml } from '@utils/browser';
import { abbreviateDate } from '@utils/date';
import { fileNameFromPath } from '@utils/strings';

import { useCalendarEvent } from 'api/calendar-events/hooks';
import { useEmailAddressOptions } from 'api/company/hooks';
import {
  useActivityLogs,
  useJourneyTravellerAction,
  useUpdateJourneyTravellerAction,
} from 'api/traveller-actions/hooks';
import { TravellerJourneyActionDetailed } from 'api/types';
import { ACTION_TYPES } from 'ui/actions/action-config';
import { getActionStatus } from 'ui/authenticated/main/helpers';
import { actionErrorTitle } from 'ui/traveller-journey-builder/node-types/common';

import { CalendarEventData, CalendarEventFormFields, formatFormData } from './common';

import {
  ActionProgressBar,
  ActionResultCard,
  FormSidebarFooter,
  SidebarProps,
  TravellerActionActions,
  TravellerActionFooter,
  TravellerActionHeader,
  isActionError,
  useActionTabs,
  useEditingContext,
} from '../common';
import { EmailAddressOptions } from '../shared/send-from-email-select';
import { ActionTimeWithTimezoneTooltip } from '../timezone';

const ACTION_TYPE = ACTION_TYPES.calendar_event;

export const SidebarContent = ({ shortId }: SidebarProps) => {
  const { t } = useScopedTranslate(sidebarLocale);
  const travellerActionQuery = useJourneyTravellerAction<typeof ACTION_TYPE>(shortId);
  const emailAddressOptions = useEmailAddressOptions();
  const { data: activityLogs } = useActivityLogs(shortId);
  const activityCount = activityLogs?.length ?? 0;
  const action = travellerActionQuery.data;

  const { tabs } = useActionTabs({
    actionId: shortId ?? '',
    activityCount,
    display: action ? <DetailsContent action={action} /> : <></>,
    edit: action ? (
      <FormContent action={action} emailAddressOptions={emailAddressOptions} />
    ) : (
      <></>
    ),
  });

  if (travellerActionQuery.isLoading || emailAddressOptions.isLoading) {
    return <Spinner />;
  }
  if (!shortId || !action) {
    return <div>{t('dataNotFound')}</div>;
  }

  return (
    <Sidebar>
      <TravellerActionHeader actionType={ACTION_TYPE} tabs={tabs}>
        <TravellerActionActions action={action} />
      </TravellerActionHeader>
    </Sidebar>
  );
};

type ContentProps = {
  action: TravellerJourneyActionDetailed<typeof ACTION_TYPE>;
};

const DetailsContent = ({ action }: ContentProps) => {
  const calendarEventQuery = useCalendarEvent(action.shortId);
  const { t } = useScopedTranslate(sidebarLocale);
  const isError = isActionError(action);

  return (
    <Stack space={6}>
      <ActionProgressBar action={action} isError={isError} isCard={false} />
      <Sidebar.Separator />
      <HideContainer title={t('actionDetails')}>
        <Stack space={4}>
          <LabelTextGroup label={t('description')} text={action.description} />
          <CalendarEventDetailCard action={action} hideStatus />
        </Stack>
      </HideContainer>
      <Sidebar.Separator />
      <HideContainer title={t('calendarEventOutput')}>
        {calendarEventQuery.data?.file ? (
          <UploadedFile
            name={fileNameFromPath(calendarEventQuery.data.file)}
            path={calendarEventQuery.data.file}
            iconColor="elementPurpleInvert"
            font="regular14"
          />
        ) : (
          <NoResultCard icon={IconDate} text={t('actionNotRun')} />
        )}
      </HideContainer>
      <TravellerActionFooter
        action={action}
        startIcon={<IconSend />}
        startText={t('create')}
        retryText={t('retry')}
      />
    </Stack>
  );
};

type CalendarEventDetailCardProps = ContentProps & {
  hideStatus?: boolean;
};

const CalendarEventDetailCard = ({ action, hideStatus }: CalendarEventDetailCardProps) => {
  const { t } = useScopedTranslate(sidebarLocale);

  const isError = isActionError(action);
  const status = getActionStatus(action);

  const durationMinutes = action.data?.durationMinutes;
  const durationHours = action.data?.durationHours;
  const [offset] = action?.timezone?.split(' ') ?? [''];

  const progressText =
    status === 'completed'
      ? t('calendarEventCreated')
      : isError
        ? t('error')
        : status === 'in_progress'
          ? t('inProgress')
          : t('notStarted');

  const referenceDate = action.data.date ? new Date(action.data.date) : undefined;
  if (referenceDate) {
    referenceDate.setHours(action.data?.hour);
    referenceDate.setMinutes(action.data?.minute);
  }
  const emailAddressOptions = useEmailAddressOptions();
  const emailAddressUsed = emailAddressOptions.options.find(
    (option) => option.value === action.data.sendFromEmailId,
  );
  const sendFromAddress = emailAddressUsed
    ? `${emailAddressUsed.label as string} <${emailAddressUsed.helpText}>`
    : t('systemDefaultEmail');
  const description = action.data?.description;

  const labelTextGroups = {
    title: { label: t('title'), text: getRawTextFromHtml(action.data?.title) },
    description: {
      label: t('description'),
      text: description ? getRawTextFromHtml(description) : '',
    },
    location: { label: t('location'), text: action.data?.location },
    organizer: { label: t('organizer'), text: action.data?.organizer },
    attendees: { label: t('attendees'), text: action.data?.attendees },
    sendEmailToAttendees: {
      label: t('sendEmailToAttendees'),
      text: action.data?.sendEmailToAttendees ? t('core.yes') : t('core.no'),
    },
    sendFrom: { label: t('sendFrom'), text: sendFromAddress },
    date: {
      label: t('date'),
      text: referenceDate ? abbreviateDate(referenceDate, 'en', 'datetime') : undefined,
    },
  };
  return (
    <ActionResultCard
      action={action}
      title={t('calendarEvent')}
      icon={<IconDate />}
      progressText={progressText}
      errorTitle={actionErrorTitle[action.action]}
      hideStatus={hideStatus}
    >
      {Object.entries(labelTextGroups).map(([key, { label, text }]) => {
        if (!text) {
          return <LabelTextGroup key={key} label={label} />;
        }
        if (key !== 'date') {
          return <LabelTextGroup key={key} label={label} text={text} />;
        }
        return (
          <div key={key}>
            <Text font="semibold14" text={label} />
            <ActionTimeWithTimezoneTooltip
              actionText={text}
              hour={action?.values?.hour?.value as string}
              minute={action?.values?.minute?.value as string}
              journeyTimezoneOffset={offset}
            />
          </div>
        );
      })}

      <Stack space={1}>
        <Text font="semibold14" text="Duration" />

        {durationMinutes === '0' && durationHours === '0' ? (
          <LabelTextGroup label="" />
        ) : (
          <>
            <Text
              font="semibold14"
              text={
                durationHours === '1' ? t('hourConfig') : t('hoursConfig', { total: durationHours })
              }
            />
            <Text
              font="semibold14"
              text={
                durationMinutes === '1'
                  ? t('minuteConfig')
                  : t('minutesConfig', { total: durationMinutes })
              }
            />
          </>
        )}
      </Stack>
    </ActionResultCard>
  );
};

const Form = createForm<CalendarEventData>();

type FormContentProps = ContentProps & {
  emailAddressOptions: EmailAddressOptions;
};

const FormContent = ({ action, emailAddressOptions }: FormContentProps) => {
  const formStore = useCreateFormStore();
  const isDirty = formStore.useIsDirty();
  const { stopEditing } = useEditingContext();
  const editMutation = useUpdateJourneyTravellerAction(action, {
    onSuccess: stopEditing,
  });

  const handleSave = (data: CalendarEventData) => {
    return editMutation.mutateAsync(formatFormData(data));
  };

  return (
    <FormWithUnsavedChangesPrompt store={formStore} onSubmit={handleSave}>
      <CalendarEventFormFields
        Form={Form}
        emailAddressOptions={emailAddressOptions}
        data={action}
      />
      <FormSidebarFooter Form={Form} isDirty={isDirty} editMutation={editMutation} />
    </FormWithUnsavedChangesPrompt>
  );
};

const sidebarLocale = {
  en: {
    calendarEvent: 'Calendar event',
    dataNotFound: 'Data not found',
    description: 'Description',
    actionDetails: 'Action details',
    inProgress: 'In progress',
    calendarEventCreated: 'Calendar event created',
    error: 'Error',
    notStarted: 'Not started',
    title: 'Title',
    location: 'Location',
    organizer: 'Organizer',
    attendees: 'Attendees',
    date: 'Date',
    atHour: 'At hour',
    atMinute: 'At minutes',
    duration: 'Duration',
    calendarEventOutput: 'Calendar event output',
    actionNotRun: 'Action has not been run',
    hourConfig: '1 • Hour',
    hoursConfig: '{total} • Hours',
    minuteConfig: '1 • Minute',
    minutesConfig: '{total} • Minutes',
    create: 'Create',
    retry: 'Re-create',
    sendEmailToAttendees: 'Send email to attendees?',
    sendFrom: 'Send from',
    systemDefaultEmail: 'System default email',
  },
};
